import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Hook to manage age gate state.
 * Returns whether the age gate / re-verification modal should show,
 * and convenience getters for age flags.
 */
export const useAgeGate = () => {
  const { user } = useAuth();
  const [needsAgeGate, setNeedsAgeGate] = useState(false);
  const [needsReverification, setNeedsReverification] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkAgeGate = useCallback(async () => {
    setLoading(true);
    let birthMonth: number | null = null;
    let lastVerifiedYear: number | null = null;
    let is18Plus = false;

    if (user) {
      const { data } = await (supabase.from("profiles") as any)
        .select("birth_month, is_15_plus, is_18_plus, last_verified_year")
        .eq("id", user.id)
        .single();

      if (data) {
        birthMonth = data.birth_month;
        lastVerifiedYear = data.last_verified_year;
        is18Plus = data.is_18_plus ?? false;

        // Hydrate localStorage
        if (birthMonth) localStorage.setItem("ayeris_birth_month", String(birthMonth));
        if (data.is_15_plus !== null) localStorage.setItem("ayeris_is_15_plus", String(data.is_15_plus));
        if (data.is_18_plus !== null) localStorage.setItem("ayeris_is_18_plus", String(data.is_18_plus));
        if (lastVerifiedYear) localStorage.setItem("ayeris_last_verified_year", String(lastVerifiedYear));
      }
    } else {
      // Guest — check localStorage
      const bm = localStorage.getItem("ayeris_birth_month");
      birthMonth = bm ? parseInt(bm) : null;
      const lvy = localStorage.getItem("ayeris_last_verified_year");
      lastVerifiedYear = lvy ? parseInt(lvy) : null;
      is18Plus = localStorage.getItem("ayeris_is_18_plus") === "true";
    }

    // If no birth_month set, user hasn't done the age gate yet
    if (!birthMonth) {
      setNeedsAgeGate(true);
      setNeedsReverification(false);
      setLoading(false);
      return;
    }

    // Already 18+? No re-verification needed ever.
    if (is18Plus) {
      setNeedsAgeGate(false);
      setNeedsReverification(false);
      setLoading(false);
      return;
    }

    // Check if re-verification is needed:
    // Only trigger on or after the LAST day of the birth month, and only once per year
    // (last_verified_year is updated on completion, preventing repeat prompts)
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 1-indexed
    const currentDay = now.getDate();

    // Last day of birth month in current year
    const lastDayOfBirthMonth = new Date(currentYear, birthMonth, 0).getDate();

    const pastEndOfBirthMonth =
      currentMonth > birthMonth ||
      (currentMonth === birthMonth && currentDay >= lastDayOfBirthMonth);

    if (
      lastVerifiedYear &&
      currentYear > lastVerifiedYear &&
      pastEndOfBirthMonth
    ) {
      setNeedsReverification(true);
      setNeedsAgeGate(false);
    } else {
      setNeedsReverification(false);
      setNeedsAgeGate(false);
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    checkAgeGate();
  }, [checkAgeGate]);

  const getIs15Plus = (): boolean => {
    return localStorage.getItem("ayeris_is_15_plus") === "true";
  };

  const getIs18Plus = (): boolean => {
    return localStorage.getItem("ayeris_is_18_plus") === "true";
  };

  return {
    needsAgeGate,
    needsReverification,
    loading,
    checkAgeGate,
    getIs15Plus,
    getIs18Plus,
    setNeedsAgeGate,
    setNeedsReverification,
  };
};
